// src/components/settings/InstagramAutomatedSetup.jsx - Frontend automation integration
import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import {
    CheckCircleIcon,
    ExclamationTriangleIcon,
    ArrowPathIcon,
    ClockIcon,
    UserIcon,
    HeartIcon,
    InformationCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { automationAPI } from '../../services/api';
import InstagramOnboardingWizard from "./InstagramOnboardingWizard.jsx"

// Main automated setup component
function InstagramAutomatedSetup({ onComplete, onBack }) {
    const [accessToken, setAccessToken] = useState('');
    const [discoveredAccounts, setDiscoveredAccounts] = useState([]);
    const [selectedAccountId, setSelectedAccountId] = useState('');
    const [setupStage, setSetupStage] = useState('token-input'); // token-input, discovering, account-selection, setting-up, complete

    // Auto-discovery mutation
    const discoverMutation = useMutation(automationAPI.autoDiscover, {
        onSuccess: (response) => {
            const accounts = response.data.accounts;
            setDiscoveredAccounts(accounts);

            if (accounts.length === 0) {
                toast.error('No Instagram Business accounts found. Please check your account settings.');
                setSetupStage('token-input');
            } else if (accounts.length === 1) {
                // Auto-select single account and proceed
                setSelectedAccountId(accounts[0].instagramId);
                setSetupStage('setting-up');
                autoSetupMutation.mutate({
                    accessToken,
                    selectedAccountId: accounts[0].instagramId
                });
            } else {
                // Multiple accounts - let user choose
                setSetupStage('account-selection');
            }
        },
        onError: (error) => {
            console.error('Auto-discovery failed:', error);
            toast.error(error.response?.data?.message || 'Failed to discover Instagram accounts');
            setSetupStage('token-input');
        }
    });

    // Auto-setup mutation
    const autoSetupMutation = useMutation(automationAPI.autoSetup, {
        onSuccess: (response) => {
            toast.success(response.message);
            setSetupStage('complete');
            // Call parent completion handler after a short delay to show success
            setTimeout(() => {
                onComplete?.(response.data);
            }, 2000);
        },
        onError: (error) => {
            console.error('Auto-setup failed:', error);
            toast.error(error.response?.data?.message || 'Automated setup failed');
            setSetupStage('token-input');
        }
    });

    // Token validation mutation
    const validateTokenMutation = useMutation(automationAPI.validateToken, {
        onSuccess: (response) => {
            const validation = response.data;
            if (!validation.isValid) {
                toast.error('Invalid access token');
                return;
            }

            if (validation.missingScopes.length > 0) {
                toast.error(`Missing permissions: ${validation.missingScopes.join(', ')}`);
                return;
            }

            if (validation.needsRefresh) {
                toast.warn(`Token expires in ${validation.daysUntilExpiry} days`);
            }

            // Token is valid, proceed with discovery
            setSetupStage('discovering');
            discoverMutation.mutate({ accessToken });
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Token validation failed');
        }
    });

    const handleTokenSubmit = async () => {
        if (!accessToken.trim()) {
            toast.error('Please enter your access token');
            return;
        }

        // ADD THESE VALIDATIONS:
        const token = accessToken.trim();

        if (token.length < 50) {
            toast.error('Access token appears too short. Please check you copied the complete token.');
            return;
        }

        if (!token.startsWith('IGA') && !token.startsWith('EAA')) {
            toast.warn('Token format unusual - Instagram tokens typically start with IGA or EAA');
        }

        // Check for common copy-paste issues
        if (token.includes(' ') || token.includes('\n') || token.includes('\t')) {
            toast.error('Token contains spaces or line breaks. Please copy the token carefully.');
            return;
        }

        // Validate token first, then auto-discover
        validateTokenMutation.mutate({ accessToken: accessToken.trim() });
    };

    const handleAccountSelection = () => {
        if (!selectedAccountId) {
            toast.error('Please select an Instagram account');
            return;
        }

        setSetupStage('setting-up');
        autoSetupMutation.mutate({
            accessToken,
            selectedAccountId
        });
    };

    const isLoading = discoverMutation.isLoading || autoSetupMutation.isLoading || validateTokenMutation.isLoading;

    return (
        <div className="space-y-6">
            {/* Token Input Stage */}
            {setupStage === 'token-input' && (
                <div className="space-y-4">
                    <div className="text-center">
                        <div className="text-4xl mb-4">🤖</div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                            Automated Instagram Setup
                        </h3>
                        <p className="text-gray-600">
                            Just paste your access token and we'll handle the rest automatically!
                        </p>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="text-sm text-blue-800">
                            <p className="font-medium mb-1">✨ What we'll do automatically:</p>
                            <ul className="list-disc list-inside space-y-1">
                                <li>Validate your access token and permissions</li>
                                <li>Discover all your Instagram Business accounts</li>
                                <li>Select the best account (or let you choose)</li>
                                <li>Save credentials securely</li>
                                <li>Test the connection</li>
                            </ul>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Instagram Access Token (Long-lived, 60 days)
                        </label>
                        <textarea
                            value={accessToken}
                            onChange={(e) => setAccessToken(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Paste your long-lived access token here..."
                            rows={3}
                            style={{ fontFamily: 'monospace', fontSize: '12px' }}
                        />
                        <p className="mt-1 text-sm text-gray-500">
                            Get this from the Graph API Explorer → Access Token Debugger
                        </p>
                        {accessToken && (
                            <div className="mt-2 text-xs">
                                <span className="text-gray-500">Length: </span>
                                <span className={accessToken.length > 100 ? 'text-green-600' : 'text-red-600'}>
                                    {accessToken.length} characters
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-between">
                        <button
                            onClick={onBack}
                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                        >
                            ← Manual Setup
                        </button>

                        <button
                            onClick={handleTokenSubmit}
                            disabled={isLoading || !accessToken.trim()}
                            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                            {isLoading ? (
                                <>
                                    <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                                    Processing...
                                </>
                            ) : (
                                <>
                                    🚀 Start Auto-Setup
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Discovering Stage */}
            {setupStage === 'discovering' && (
                <div className="text-center space-y-4">
                    <div className="text-4xl mb-4">🔍</div>
                    <h3 className="text-lg font-medium text-gray-900">
                        Discovering Your Instagram Accounts
                    </h3>
                    <p className="text-gray-600">
                        Automatically scanning for Instagram Business accounts connected to your Facebook Pages...
                    </p>
                    <div className="animate-pulse bg-gray-200 h-2 rounded-full w-3/4 mx-auto"></div>
                </div>
            )}

            {/* Account Selection Stage */}
            {setupStage === 'account-selection' && (
                <div className="space-y-4">
                    <div className="text-center">
                        <div className="text-4xl mb-4">📋</div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                            Select Instagram Account
                        </h3>
                        <p className="text-gray-600">
                            We found {discoveredAccounts.length} Instagram Business accounts. Choose which one to use:
                        </p>
                    </div>

                    <div className="space-y-3">
                        {discoveredAccounts.map((account) => (
                            <div
                                key={account.instagramId}
                                className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                                    selectedAccountId === account.instagramId
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-200 hover:border-gray-300'
                                }`}
                                onClick={() => setSelectedAccountId(account.instagramId)}
                            >
                                <div className="flex items-center space-x-3">
                                    <input
                                        type="radio"
                                        checked={selectedAccountId === account.instagramId}
                                        onChange={() => setSelectedAccountId(account.instagramId)}
                                        className="text-blue-600"
                                    />

                                    {account.profilePicture ? (
                                        <img
                                            src={account.profilePicture}
                                            alt={account.username}
                                            className="w-12 h-12 rounded-full"
                                        />
                                    ) : (
                                        <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                                            <UserIcon className="h-6 w-6 text-gray-500" />
                                        </div>
                                    )}

                                    <div className="flex-1">
                                        <div className="flex items-center space-x-2">
                                            <h4 className="font-medium text-gray-900">@{account.username}</h4>
                                            {account.name && (
                                                <span className="text-gray-600">({account.name})</span>
                                            )}
                                        </div>

                                        <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                                            <span className="flex items-center">
                                                <HeartIcon className="h-4 w-4 mr-1" />
                                                {account.followers?.toLocaleString() || 0} followers
                                            </span>
                                            <span>{account.mediaCount || 0} posts</span>
                                        </div>

                                        <p className="text-xs text-gray-500 mt-1">
                                            Connected to: {account.connectedPage.name}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="flex justify-between">
                        <button
                            onClick={() => setSetupStage('token-input')}
                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                        >
                            ← Back
                        </button>

                        <button
                            onClick={handleAccountSelection}
                            disabled={!selectedAccountId || isLoading}
                            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Continue with Selected Account
                        </button>
                    </div>
                </div>
            )}

            {/* Setting Up Stage */}
            {setupStage === 'setting-up' && (
                <div className="text-center space-y-4">
                    <div className="text-4xl mb-4">⚙️</div>
                    <h3 className="text-lg font-medium text-gray-900">
                        Completing Setup
                    </h3>
                    <p className="text-gray-600">
                        Saving credentials, testing connection, and finalizing your Instagram integration...
                    </p>
                    <div className="animate-pulse bg-gray-200 h-2 rounded-full w-2/3 mx-auto"></div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
                        <div className="text-sm text-blue-800 space-y-1">
                            <p className="flex items-center">
                                <CheckCircleIcon className="h-4 w-4 mr-2 text-blue-600" />
                                Access token validated
                            </p>
                            <p className="flex items-center">
                                <CheckCircleIcon className="h-4 w-4 mr-2 text-blue-600" />
                                Instagram account discovered
                            </p>
                            <p className="flex items-center">
                                <ArrowPathIcon className="h-4 w-4 mr-2 text-blue-600 animate-spin" />
                                Saving credentials securely...
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Complete Stage */}
            {setupStage === 'complete' && (
                <div className="text-center space-y-4">
                    <div className="text-6xl mb-4">🎉</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                        Instagram Setup Complete!
                    </h3>
                    <p className="text-gray-600">
                        Your Instagram account has been successfully connected to PostMgr using automated setup.
                    </p>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="text-sm text-green-800 space-y-2">
                            <p className="font-medium">✅ Setup completed automatically:</p>
                            <ul className="text-left space-y-1 ml-4">
                                <li>• Access token validated and saved</li>
                                <li>• Instagram Business account discovered</li>
                                <li>• Connection tested successfully</li>
                                <li>• Ready to start posting!</li>
                            </ul>
                        </div>
                    </div>

                    <div className="text-sm text-gray-500">
                        Redirecting you back to settings in a moment...
                    </div>
                </div>
            )}
        </div>
    );
}

// Integration with main Instagram wizard

export function InstagramWizardWithAutomation({ onComplete, onClose }) {
    const [setupMode, setSetupMode] = useState('choose'); // choose, manual, automated

    if (setupMode === 'choose') {
        return (
            <div className="space-y-6">
                <div className="text-center">
                    <div className="text-6xl mb-4">📸</div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                        Choose Your Instagram Setup Method
                    </h2>
                    <p className="text-gray-600 max-w-lg mx-auto">
                        Choose between our lightning-fast automated setup or detailed manual guide
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <button
                        onClick={() => setSetupMode('automated')}
                        className="group p-6 border-2 border-blue-200 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-all duration-200 text-left relative overflow-hidden"
                    >
                        <div className="absolute top-2 right-2 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                            ⚡ Fastest
                        </div>
                        <div className="text-4xl mb-4">🤖</div>
                        <h3 className="font-bold text-gray-900 mb-2">Automated Setup</h3>
                        <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                            Just paste your access token and we'll automatically discover your accounts,
                            find your Business ID, and complete the setup.
                        </p>
                        <div className="flex items-center justify-between">
                            <div className="text-xs text-green-600 font-medium bg-green-100 px-2 py-1 rounded">
                                ✨ Recommended
                            </div>
                            <div className="text-xs text-gray-500">~30 seconds</div>
                        </div>
                        <div className="mt-3 text-xs text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                            → Perfect for quick setup
                        </div>
                    </button>

                    <button
                        onClick={() => setSetupMode('manual')}
                        className="group p-6 border-2 border-gray-200 rounded-xl hover:border-gray-400 hover:bg-gray-50 transition-all duration-200 text-left relative overflow-hidden"
                    >
                        <div className="absolute top-2 right-2 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full font-medium">
                            📋 Detailed
                        </div>
                        <div className="text-4xl mb-4">🔧</div>
                        <h3 className="font-bold text-gray-900 mb-2">Manual Guide</h3>
                        <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                            Complete step-by-step walkthrough with screenshots and detailed instructions
                            for creating apps and tokens.
                        </p>
                        <div className="flex items-center justify-between">
                            <div className="text-xs text-gray-600 font-medium bg-gray-100 px-2 py-1 rounded">
                                🎓 Educational
                            </div>
                            <div className="text-xs text-gray-500">~5-10 minutes</div>
                        </div>
                        <div className="mt-3 text-xs text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity">
                            → Learn the full process
                        </div>
                    </button>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start">
                        <InformationCircleIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
                        <div className="text-sm text-blue-800">
                            <p className="font-medium mb-1">Both methods require:</p>
                            <ul className="list-disc list-inside space-y-1">
                                <li>Instagram Business or Creator account</li>
                                <li>Connected to a Facebook Page</li>
                                <li>Meta Developer account (free)</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (setupMode === 'automated') {
        return (
            <InstagramAutomatedSetup
                onComplete={onComplete}
                onBack={() => setSetupMode('choose')}
            />
        );
    }

    if (setupMode === 'manual') {
        // Return the updated manual wizard component that works within our modal
        return (
            <InstagramOnboardingWizard
                onComplete={onComplete}
                onClose={onClose}
                onBack={() => setSetupMode('choose')}
            />
        );
    }
}

export default InstagramAutomatedSetup;